import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import type { TrackedTranslation } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import type { FormApi } from 'final-form';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DeAddressFormType, styles as sharedStyles } from '@swf/account';
import MitIdRedirect from '@swf/components/MitIdRedirect/MitIdRedirect';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { CrossOperatorDepositLimitForm } from '@swf/notification';

import useMitId from '../hooks/useMitId';

import AddressFields from './AddressFields/AddressFields';
import AddressFieldsCZ from './AddressFieldsCZ/AddressFieldsCZ';
import styles from './AddressForm.scss';
import AddressFormLayout from './AddressFormLayout/AddressFormLayout';
import AddressFormNotificationHandler from './AddressFormNotificationHandler/AddressFormNotificationHandler';
import StreetAddressDefault from './StreetAddressDefault/StreetAddressDefault';
import StreetAddressExtended from './StreetAddressExtended/StreetAddressExtended';
import type { AddressFormValues } from './useAddressUpdate';
import { useAddressUpdate } from './useAddressUpdate';

interface Props {
  submitButtonText?: TrackedTranslation;
  successCallback?: () => void;
  showStates?: boolean;
  disabled?: boolean;
  restrictedWidth?: boolean;
}

const restrictedCountries = ['AR'];
const restrictedStates = [
  'B', // Buenos Aires
  'C' // CABA
];

const AddressForm: React.FC<Props> = ({
  submitButtonText,
  showStates = true,
  successCallback,
  disabled,
  restrictedWidth = true
}) => {
  const isEmbedded = useSelector(select.device.isEmbedded);
  const site = useSelector(select.cms.getSiteId);
  const state = useSelector(select.account.getAddressState) as string;
  const country = useSelector(select.account.getCountry) as string;
  const disableBuenosAiresFlag = useFeature(FeatureFlag.DisableBuenosAiresFeatures);
  const euDeOasisStreetAddress = useFeature(FeatureFlag.EuDeOasisStreetAddress);
  const formApiRef = useRef<FormApi<AddressFormValues>>(undefined);
  const [formType, setFormType] = useState<DeAddressFormType>(DeAddressFormType.Address);
  const [limit, setLimit] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>(false);
  const searchParams = useSearchParams<object>();

  const lugasReregistrationCallback = (value: DeAddressFormType) => {
    setFormType(value);
  };

  const {
    account: { updateAccount }
  } = useDispatch<Dispatch>();
  const { mitIdError, isMitIdRequired, mitIdData, mitIdSubmit } =
    useMitId<AddressFormValues>(updateAccount);
  const onAddressUpdate = useAddressUpdate({
    onUpdate: mitIdSubmit,
    successCallback,
    lugasReregistrationCallback
  });

  useEffect(() => {
    if (mitIdData?.city && country) {
      formApiRef.current?.submit();
    }
  }, [country, mitIdData]);

  useEffect(() => {
    if (limit) {
      setLimit(false);
      setFormType(DeAddressFormType.Address);
      onAddressUpdate(searchParams as AddressFormValues);
    }
  }, [limit, onAddressUpdate, searchParams]);

  /* If in Buenos Aires, we should disable the State Field.
  We can turn this functionality on and off using the `DisableBuenosAiresFeatures` flag.
  If `DisableBuenosAiresFeatures` is true, state field will remain active. */
  const isRestrictedInBuenosAires =
    !disableBuenosAiresFlag &&
    restrictedCountries.includes(country) &&
    restrictedStates.includes(state);

  const isSiteCZ = site === Sites.CZE;

  return formType === DeAddressFormType.CrossOperatorLimit ? (
    <CrossOperatorDepositLimitForm
      setLimitSuccess={setLimit}
      loading={loading}
      setLoading={setLoading}
      restrictedWidth={restrictedWidth}
    />
  ) : (
    <AddressFormLayout
      submitButtonText={submitButtonText}
      onSubmit={onAddressUpdate}
      initialData={mitIdData}
    >
      {(formProps, submitButton) => {
        formApiRef.current = formProps.form;
        return (
          <>
            {!isEmbedded && isMitIdRequired && (
              <MitIdRedirect returnModal="/account/details/address/" values={formProps.values} />
            )}
            <AddressFormNotificationHandler
              submitSucceeded={formProps.submitSucceeded}
              isMitIdRequired={isMitIdRequired}
              submitError={formProps.submitError}
              mitIdError={mitIdError}
            />
            <div
              className={classNames(
                restrictedWidth && sharedStyles.restrictedWidth,
                styles.formFields
              )}
            >
              {isSiteCZ ? (
                <AddressFieldsCZ disabled={disabled} showStates={showStates} />
              ) : (
                <AddressFields
                  disabled={disabled}
                  showStates={showStates}
                  restricted={isRestrictedInBuenosAires}
                >
                  {euDeOasisStreetAddress ? (
                    <StreetAddressExtended disabled={disabled || isRestrictedInBuenosAires} />
                  ) : (
                    <StreetAddressDefault disabled={disabled || isRestrictedInBuenosAires} />
                  )}
                </AddressFields>
              )}
              {submitButton}
            </div>
          </>
        );
      }}
    </AddressFormLayout>
  );
};

export default AddressForm;
